'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'
import { Button } from '@/components/ui/button'
import type { NavItem } from '@/utilities/resolveNavItems'

/* --------------------------------------------------------------------------
 * Figma parity — Navigation `4114:178153`
 *
 * Header bar        desktop 1600×64 px-16 · tablet 768×64 px-8 · mobile 390×56 p-4
 * Logo              208×32 (tablet+) · 156×24 (mobile)
 * Nav item          "link button" 2198:34729 — Body/bodyMdAlt: Lato regular 400,
 *                   font/size/base 16 with line-height bound to font/size/lg
 *                   (24 desktop / 22 tablet / 20 mobile),
 *                   theme/foreground #000, padding 0, gap-1 (4px) to the chevron,
 *                   radius sm (2px), NO underline. Row gap 16px.
 * CTA               button size "Mini" — h 24, p-2 (8), gap-1, bg theme/accent,
 *                   label 14/16 regular, theme/accent-foreground, radius sm.
 * Dropdown panel    4114:177988 — bg background, 1px theme/border, radius sm,
 *                   p-2 (8), gap-2 (8), shadow/DEFAULT (= Tailwind shadow-md).
 * Menu item         2213:20250 — w 200, p-2 (8), gap-2, radius sm,
 *                   Body/bodyMd (Lato light 300, 16 / lh bound to font/size/lg),
 *                   hover bg = theme/secondary #74cdde.
 * Mobile / tablet   hamburger 24×24 then CTA, gap 12; menu is the same 216px
 *                   dropdown panel anchored under the header on the right.
 * ------------------------------------------------------------------------ */

/* NOTE: these class strings are composed with plain template literals rather than `cn`.
 * That was originally a workaround: tailwind-merge classified the custom `text-body-*`
 * font-size utilities into the same group as `text-foreground` and silently dropped them,
 * losing the responsive Body/* line-height.
 *
 * That root cause is now FIXED — `cn()` (src/utilities/ui.ts) registers the Wilson scale
 * under `font-size` via extendTailwindMerge, so `cn('text-body-md', 'text-foreground')`
 * correctly keeps both. These literals are therefore safe but no longer necessary; new code
 * should just use `cn`. */

/** Figma menu-item / dropdown-item shared shape (2213:20250) — weight is NOT set here. */
const MENU_ITEM_BASE =
  'min-w-[200px] rounded-sm p-2 text-body-md text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring'

/* Parent rows (the collapsible "Sell" / "Buy" / ... headings in the mobile panel) are
 * Body/bodyMdAlt — Lato REGULAR 400. Child links are Body/bodyMd — Lato LIGHT 300. The
 * weight step is what visually separates a submenu from its parent; without it the mobile
 * accordion reads as one flat list. Desktop panels contain only children, so they use the
 * light weight throughout (their parent is the nav bar item, already 400 via NAV_LINK). */
const MENU_ITEM_PARENT = `${MENU_ITEM_BASE} font-normal`
const MENU_ITEM_CHILD = `${MENU_ITEM_BASE} font-light`

const MENU_ITEM = `block ${MENU_ITEM_CHILD}`

/** Figma "Menu Dropdown" panel shape. */
const MENU_PANEL = 'flex flex-col gap-2 rounded-sm border border-border bg-background p-2 shadow-md'

// The header CTA is Figma button type=primary, size=Mini — which the canonical Button now
// expresses directly (`variant="default" size="mini"`). The previous hand-rolled CTA_BUTTON
// class string was a clone of buttonVariants() that had drifted: it carried `btn-underline`
// (a sliding-underline affordance Figma does not define on any button) and
// `hover:bg-accent/90` instead of Figma's hover swap to theme/secondary.

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

/** Top-level link colours — Figma: theme/foreground on the white bar. */
function navLinkColor(scrolled: boolean) {
  return scrolled
    ? 'text-foreground hover:text-[var(--color-link-hover)]'
    : 'text-white hover:text-white/80'
}

/* `text-body-md` carries Body/bodyMdAlt's responsive line-height (bound to font/size/lg →
 * 20 mobile / 22 tablet / 24 desktop). Do NOT pin it with a `leading-*` utility. */
const NAV_LINK =
  'inline-flex shrink-0 items-center gap-1 self-stretch rounded-sm py-0 text-body-md font-normal transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring'

function DesktopDropdown({
  item,
  pathname,
  scrolled,
  open,
  setOpen,
}: {
  item: NavItem
  pathname: string
  scrolled: boolean
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setOpen(true)
  }

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150)
  }

  useEffect(() => {
    setOpen(false)
    // setOpen is stable (parent useCallback); pathname is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const isActive = pathname.startsWith(item.href)

  return (
    <div
      ref={ref}
      className="relative flex self-stretch"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {/* Asana 012 — parent items are dropdown triggers only, never links.
          Click toggles the menu (keyboard/touch parity with the hover-open). */}
      <button
        className={`${NAV_LINK} ${navLinkColor(scrolled)}`}
        aria-expanded={open}
        aria-haspopup="true"
        aria-current={isActive ? 'page' : undefined}
        onClick={() => setOpen(!open)}
      >
        <span>{item.label}</span>
        <ChevronDown
          className={cn('size-4 shrink-0 transition-transform duration-150', open && 'rotate-180')}
        />
      </button>

      {open && item.children && item.children.length > 0 && (
        <div className={`absolute top-full left-0 z-50 ${MENU_PANEL}`}>
          {item.children.map((child) => (
            <Link
              key={child.href}
              href={child.href}
              target={child.newTab ? '_blank' : undefined}
              rel={child.newTab ? 'noopener noreferrer' : undefined}
              className={MENU_ITEM}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function MobileDropdown({
  item,
  pathname,
  onNavigate,
  open,
  onToggle,
}: {
  item: NavItem
  pathname: string
  onNavigate: () => void
  open: boolean
  onToggle: () => void
}) {
  const isActive = pathname.startsWith(item.href)

  const hasChildren = Boolean(item.children && item.children.length > 0)

  return (
    <div className="flex flex-col gap-2">
      {/* Asana 012 — a parent with children toggles its submenu (whole row),
          never navigates; childless items stay real links. */}
      {hasChildren ? (
        <button
          onClick={onToggle}
          className={`flex w-full items-center gap-2 text-left ${MENU_ITEM_PARENT}`}
          aria-expanded={open}
          aria-current={isActive ? 'page' : undefined}
        >
          <span className="min-w-0 flex-1">{item.label}</span>
          <ChevronDown
            className={cn('size-4 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>
      ) : (
        <div className={`flex items-center gap-2 ${MENU_ITEM_PARENT}`}>
          <Link
            href={item.href}
            target={item.newTab ? '_blank' : undefined}
            rel={item.newTab ? 'noopener noreferrer' : undefined}
            onClick={onNavigate}
            aria-current={isActive ? 'page' : undefined}
            className="min-w-0 flex-1"
          >
            {item.label}
          </Link>
        </div>
      )}
      {open && item.children && (
        <div className="flex flex-col gap-2">
          {item.children.map((child) => (
            <Link
              key={child.href}
              href={child.href}
              target={child.newTab ? '_blank' : undefined}
              rel={child.newTab ? 'noopener noreferrer' : undefined}
              onClick={onNavigate}
              aria-current={pathname.startsWith(child.href) ? 'page' : undefined}
              className={MENU_ITEM}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export function HeaderNav({
  items,
  scrolled,
  onMenuOpenChange,
  closeSignal,
}: {
  items: NavItem[]
  scrolled: boolean
  /** Lets the header drop its shadow while a menu is open — the backdrop blur
   *  already separates the bar from the page, so the shadow double-counts. */
  onMenuOpenChange?: (open: boolean) => void
  /** Bumped by HeaderClient when the blurred backdrop is tapped (Asana 006) */
  closeSignal?: number
}) {
  const pathname = usePathname()
  const [mobileOpen, setMobileOpen] = useState(false)

  /* Exactly ONE dropdown may be expanded at a time, on both breakpoints — opening a
   * second one collapses the first. Both are tracked here as "which href is open"
   * rather than as per-item booleans, which is what makes that mutual exclusion free. */
  const [openDesktopHref, setOpenDesktopHref] = useState<string | null>(null)
  const [openMobileHref, setOpenMobileHref] = useState<string | null>(null)

  const regularItems = items.filter((item) => !item.persistentCta)
  const ctaItem = items.find((item) => item.persistentCta)

  // Collapse any expanded submenu when the panel itself closes, so reopening the menu
  // never restores a stale expansion.
  useEffect(() => {
    if (!mobileOpen) setOpenMobileHref(null)
  }, [mobileOpen])

  // Backdrop tapped (Asana 006 comment) — dismiss whichever menu is open
  useEffect(() => {
    if (!closeSignal) return
    setMobileOpen(false)
    setOpenDesktopHref(null)
  }, [closeSignal])

  /* Figma Navigation 4114:178153 covers the page behind an open menu with an "Overlay" node.
   * That overlay is rendered by HeaderClient as a SIBLING of <header> rather than here: the
   * header carries a `translate-*` utility, which makes it a containing block for fixed
   * descendants and collapsed the overlay to zero height. This just reports open/closed. */
  const menuOpen = Boolean(openDesktopHref) || mobileOpen

  useEffect(() => {
    onMenuOpenChange?.(menuOpen)
  }, [menuOpen, onMenuOpenChange])

  return (
    <>
      {/* Desktop nav — Figma switches to the hamburger at tablet (768), so the
          full nav only appears from lg (1024) up. */}
      <nav className="hidden items-center gap-4 self-stretch lg:flex" data-visual="header-nav">
        {regularItems.map((item) =>
          item.children && item.children.length > 0 ? (
            <DesktopDropdown
              key={item.href}
              item={item}
              pathname={pathname}
              scrolled={scrolled}
              open={openDesktopHref === item.href}
              // Asana 006 — close must only clear OUR menu. A functional update
              // guards the race where item A's delayed hover-close fires after
              // item B has already opened (A's stale timer was nulling the shared
              // state and collapsing B's menu — the "flash" when moving across
              // the nav at speed).
              setOpen={(next) =>
                setOpenDesktopHref((prev) => (next ? item.href : prev === item.href ? null : prev))
              }
            />
          ) : (
            <Link
              key={item.href}
              href={item.href}
              target={item.newTab ? '_blank' : undefined}
              rel={item.newTab ? 'noopener noreferrer' : undefined}
              aria-current={pathname.startsWith(item.href) ? 'page' : undefined}
              className={`${NAV_LINK} ${navLinkColor(scrolled)}`}
            >
              {item.label}
            </Link>
          ),
        )}
        {ctaItem && (
          <Button asChild variant="default" size="mini" className="shrink-0 whitespace-nowrap">
            <Link
              href={ctaItem.href}
              target={ctaItem.newTab ? '_blank' : undefined}
              rel={ctaItem.newTab ? 'noopener noreferrer' : undefined}
            >
              {ctaItem.label}
            </Link>
          </Button>
        )}
      </nav>

      {/* Mobile + tablet: hamburger then CTA, 12px apart (Figma order). */}
      <div className="flex items-center gap-3 lg:hidden">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-sm transition-colors focus-visible:ring-3 focus-visible:ring-ring focus-visible:outline-none',
            scrolled ? 'text-foreground' : 'text-white',
          )}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-6"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-6"
              aria-hidden="true"
            >
              <line x1="4" x2="20" y1="12" y2="12" />
              <line x1="4" x2="20" y1="6" y2="6" />
              <line x1="4" x2="20" y1="18" y2="18" />
            </svg>
          )}
        </button>
        {ctaItem && (
          <Button asChild variant="default" size="mini" className="shrink-0 whitespace-nowrap">
            <Link
              href={ctaItem.href}
              target={ctaItem.newTab ? '_blank' : undefined}
              rel={ctaItem.newTab ? 'noopener noreferrer' : undefined}
            >
              {ctaItem.label}
            </Link>
          </Button>
        )}
      </div>

      {/* Mobile / tablet menu — Figma "Menu Dropdown": 216px panel anchored to
          the right container edge, directly under the header. */}
      {mobileOpen && (
        <div
          className={`fixed top-14 right-4 z-50 max-h-[calc(100svh-3.5rem)] overflow-y-auto md:top-16 md:right-8 md:max-h-[calc(100svh-4rem)] lg:hidden ${MENU_PANEL}`}
          data-visual="header-mobile-menu"
        >
          {regularItems.map((item) =>
            item.children && item.children.length > 0 ? (
              <MobileDropdown
                key={item.href}
                item={item}
                pathname={pathname}
                onNavigate={() => setMobileOpen(false)}
                open={openMobileHref === item.href}
                onToggle={() => setOpenMobileHref((cur) => (cur === item.href ? null : item.href))}
              />
            ) : (
              <Link
                key={item.href}
                href={item.href}
                target={item.newTab ? '_blank' : undefined}
                rel={item.newTab ? 'noopener noreferrer' : undefined}
                onClick={() => setMobileOpen(false)}
                aria-current={pathname.startsWith(item.href) ? 'page' : undefined}
                className={MENU_ITEM}
              >
                {item.label}
              </Link>
            ),
          )}
        </div>
      )}
    </>
  )
}
